import {
  ANI_ROUTER,
  CLIENT,
  DATE_RANGE_ROUTER,
  DAY_OF_WEEK_ROUTER,
  Device,
  DNIS_ROUTER,
  HUNT_GROUP,
  MATCH_START,
  NUMBER_PLAN,
  PLAY,
  PLUGIN,
  QUEUE,
  REMOTE,
  ROUTE_START,
  SAY,
  SCRIPT,
  SIP,
  SIP_GATEWAY,
  TEAM,
  TIME_RANGE_ROUTER,
  VOICEMAIL,
  ZONE_ROUTER,
} from "callable-data-objects";
import EditDeviceHeader from "Components/Shared/EditDeviceHeader";
import MenuSearch from "Components/Shared/MenuSearch";
import { get as getWidget } from "Components/Device/Flow/constants";
import useCheckRole from "Hooks/APICheckRole";
import { useDevice } from "Hooks/Endpoints";
import React, { Fragment, useState } from "react";
import {
  Button,
  Card,
  Container,
  Header,
  Icon,
  Label,
  Menu,
  Segment,
  Table,
} from "semantic-ui-react";
import AniRouterView from "./AniRouter/AniRouterView";
import ClientView from "./Client/ClientView";
import CreateDeviceSelect from "./CreateDeviceSelect";
import DateRangeRouterView from "./DateRange/DateRangeRouterView";
import DayOfWeekRouterView from "./DayOfWeek/DayOfWeekRouterView";
import DigitsView from "./Digits/DigitsView";
import DnisRouterView from "./DnisRouter/DnisRouterView";
import HuntGroupView from "./HuntGroup/HuntGroupView";
import InboundFlow from "./InboundFlow/InboundFlow";
import OutboundFlow from "./OutboundFlow/OutboundFlow";
import PlayView from "./Play/PlayView";
import Plugin from "./Plugin/Plugin";
import Queue from "./Queue/Queue";
import RegionRouterVIew from "./RegionRouter/RegionRouterVIew";
import RemoteView from "./Remote/RemoteView";
import SayView from "./Say/SayView";
import Script from "./Script/Script";
import SipExtensionView from "./SipExtension/SipExtensionView";
import SipGatewayView from "./SipGateway/SipGatewayView";
import TeamsView from "./Teams/TeamsView";
import TimeRangeRouterView from "./TimeRange/TimeRangeRouterView";
import VoicemailView from "./Voicemail/VoicemailView";

type DevicesProps = {
  device?: Device;
  title: string;
  type: string;
};

const Devices = ({ device, title, type }: DevicesProps) => {
  const { getDeviceRoles, hasRole } = useCheckRole();
  const {
    devices = [],
    select,
    createDeviceByType,
    createDevice,
    unselect,
  } = useDevice();

  const [search, setSearch] = useState("");

  const isDevice = (type: string) => {
    return !createDevice && device && device.type === type;
  };

  const filteredDevices = devices
    .filter((d: Device) => d.type === type)
    .filter(
      (d: Device) =>
        d.name.indexOf(search) > -1 || d.extension.indexOf(search) > -1
    );

  if (device) {
    const widget = getWidget(device.type);

    return (
      <Card fluid raised color={widget.color}>
        <Card.Content style={{ padding: "3px" }}>
          <Menu color={widget.color} inverted>
            <Menu.Item header>
              <Icon name={widget.icon} /> {device.extension}
            </Menu.Item>
            <Menu.Item>{device.name}</Menu.Item>
            <Menu.Menu position='right'>
              <Menu.Item>{widget.name}</Menu.Item>
              <Menu.Item color='black' onClick={() => unselect()} header>
                <Icon name='delete' />
              </Menu.Item>
            </Menu.Menu>
          </Menu>
        </Card.Content>
        <Card.Content>
          {/* {device && <EditDeviceHeader onBack={unselect} device={device} />} */}
          {isDevice(ROUTE_START) && <InboundFlow />}
          {isDevice(MATCH_START) && <OutboundFlow />}
          {isDevice(SAY) && <SayView />}
          {isDevice(PLAY) && <PlayView />}
          {isDevice(REMOTE) && <RemoteView />}
          {isDevice(SCRIPT) && <Script />}
          {isDevice(CLIENT) && <ClientView />}
          {isDevice(QUEUE) && <Queue />}
          {isDevice(PLUGIN) && <Plugin />}
          {isDevice(SIP) && <SipExtensionView />}
          {isDevice(SIP_GATEWAY) && <SipGatewayView />}
          {isDevice(TEAM) && <TeamsView />}
          {isDevice(HUNT_GROUP) && <HuntGroupView />}
          {isDevice(ANI_ROUTER) && <AniRouterView />}
          {isDevice(DNIS_ROUTER) && <DnisRouterView />}
          {isDevice(ZONE_ROUTER) && <RegionRouterVIew />}
          {isDevice(NUMBER_PLAN) && <DigitsView />}
          {isDevice(TIME_RANGE_ROUTER) && <TimeRangeRouterView />}
          {isDevice(DAY_OF_WEEK_ROUTER) && <DayOfWeekRouterView />}
          {isDevice(DATE_RANGE_ROUTER) && <DateRangeRouterView />}
          {isDevice(VOICEMAIL) && <VoicemailView />}
        </Card.Content>
      </Card>
    );
  }

  if (createDevice) {
    return <CreateDeviceSelect />;
  }

  return (
    <Fragment>
      <Menu fluid stackable>
        <Menu.Item header>{title}</Menu.Item>
        {hasRole(getDeviceRoles(type).create) && (
          <Menu.Item
            icon='plus'
            name={`Create ${title}`}
            onClick={() => createDeviceByType(type)}
          />
        )}
        <Menu.Menu position='right'>
          <MenuSearch
            placeholder='Search...'
            onSearch={(value: string) => setSearch(value)}
            value={search}
          />
        </Menu.Menu>
      </Menu>
      <Container fluid>
        <Table celled padded stackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={2} singleLine>
                Name
              </Table.HeaderCell>
              <Table.HeaderCell width={2} singleLine>
                Extension
              </Table.HeaderCell>
              {hasRole(getDeviceRoles(type).edit) && (
                <Table.HeaderCell width={1}>Actions</Table.HeaderCell>
              )}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filteredDevices.map((device: Device) => (
              <Table.Row>
                <Table.Cell singleLine>
                  <Header as='h4'>{device.name}</Header>
                </Table.Cell>
                <Table.Cell singleLine>{device.extension}</Table.Cell>
                {hasRole(getDeviceRoles(type).edit) && (
                  <Table.Cell textAlign='left'>
                    <Button
                      onClick={() => select(device.id)}
                      primary
                      size='tiny'
                    >
                      View
                    </Button>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Container>
    </Fragment>
  );
};

export default Devices;
